/**
 * Contributing Translations Module (English)
 * 
 * Contains: Contributing page content, form fields, FAQ, commitments
 * 
 * @module contributing
 * @language en
 */

export const contributing = {
  contributing: {
    // Hero Section
    pageTitle: "Contributing to Youth Futures",
    heroSubtitle: "A collaborative research initiative dedicated to understanding and advancing youth leadership globally",
    heroDescription: "We invite institutions, organizations, researchers, members, and community partners to contribute data, insights, and expertise that strengthen our platform.",
    
    // Why Contribute Section
    whyContributeTitle: "Why Contribute?",
    whyContributeSubtitle: "Your contribution makes a real difference in understanding global youth leadership",
    
    globalImpact: "Global Impact",
    globalImpactDesc: "Your data reaches policymakers, investors, JCI members worldwide, and the global youth leadership community—creating tangible impact on how young leaders are developed and recognized.",
    
    academicRecognition: "Academic Recognition",
    academicRecognitionDesc: "Formal citation in technical reports, scientific publications, presentations at international congresses, certificates of collaboration, and recognition in social media—valid for academic CV and professional portfolios.",
    
    rigorousMethodology: "Rigorous Methodology",
    rigorousMethodologyDesc: "All contributions align with international standards (UN guidelines, academic best practices) ensuring maximum credibility for your institution and results.",
    
    openAccess: "Open Access",
    openAccessDesc: "All findings are publicly available through interactive dashboards, technical reports (PDF), data files (CSV/Excel), under Creative Commons licensing—free for non-commercial use.",
    
    formalRecognition: "Formal Recognition",
    formalRecognitionDesc: "Citation in technical reports and presentations, mention in international congresses and events, digital collaboration certificate, recognition across JCI social media channels, valid for professional credentials.",
    
    // Who Can Contribute Section
    whoCanContributeTitle: "Who Can Contribute?",
    whoCanContributeSubtitle: "Diverse perspectives strengthen our research platform",
    
    nationalOrgs: "National JCI Organizations",
    nationalOrgsDesc: "Share strategic data about leadership development, member engagement, program impact, and regional initiatives.",
    nationalOrgsDetails: "National member statistics and demographics, program and project results aligned with JCI Strategic Plan 2023-2027, regional leadership trends and insights, partnership and collaboration outcomes, training and development metrics, event attendance and engagement data, strategic initiatives and innovations.",
    nationalOrgsWhy: "Help the Observatory reflect the true diversity and impact of JCI across all regions and countries.",
    
    committees: "JCI Committees & Councils",
    committeesDesc: "Specialized committees contribute expertise on specific leadership development dimensions.",
    committeesDetails: "Development Councils (Strategic, Operational, Educational, Impact), council-specific data and metrics, best practices and methodologies, member development outcomes, training and certification results, regional performance indicators.",
    committeesWhy: "Enable the Observatory to present specialized perspectives on how JCI develops leaders across all strategic dimensions.",
    
    individualMembers: "Individual JCI Members",
    individualMembersDesc: "Every member has valuable insights and experiences to share.",
    individualMembersDetails: "Personal leadership development stories, program participation feedback and outcomes, skill development and training experiences, mentorship and networking insights, career advancement examples, community impact projects, data quality corrections and updates, methodological suggestions and feedback.",
    individualMembersWhy: "Member voices ensure the Observatory reflects real leadership journeys and lived experiences within JCI.",
    
    researchPartners: "Research Partners & Academic Institutions",
    researchPartnersDesc: "Universities, research centers, and academic groups contribute original research and analysis.",
    researchPartnersDetails: "Academic research papers and studies, longitudinal studies on JCI member development, comparative analysis with other leadership organizations, methodological reviews and validations, student research projects, institutional mapping and analysis, curriculum alignment studies, published datasets and findings.",
    researchPartnersWhy: "Academic partnerships validate the Observatory as a credible research platform aligned with international standards.",
    
    educationalInstitutions: "Educational Institutions & Training Centers",
    educationalInstitutionsDesc: "Schools, universities, vocational centers, and training organizations contribute education data.",
    educationalInstitutionsDetails: "Leadership curriculum mapping, JCI member student enrollment and outcomes, training program results, skill development metrics, graduate employment data, partnership program results, course alignment with JCI framework, certification and credential data.",
    educationalInstitutionsWhy: "Educational data reveals how JCI leadership development translates to academic and professional advancement.",
    
    governmentNgos: "Government, NGOs & Institutional Partners",
    governmentNgosDesc: "Government agencies, international organizations, and civil society partners contribute strategic data.",
    governmentNgosDetails: "Public policy alignment with JCI initiatives, government partnership outcomes, international organization collaboration data, NGO partnership effectiveness, community impact metrics, sustainability and social responsibility data, institutional recognition and endorsements, regulatory and framework alignment.",
    governmentNgosWhy: "Institutional partnerships demonstrate JCI's credibility and alignment with global development agendas.",
    
    // How to Contribute Section
    howToContributeTitle: "How to Contribute",
    howToContributeSubtitle: "Submit your contribution via email with the required information",
    
    contributionFormTitle: "Contribution Form",
    contributionFormDesc: "Submit your contribution using our form. All fields are required.",
    sendTo: "Information will be sent to:",
    
    yourInformation: "Your Information",
    fullName: "Full Name",
    fullNameDesc: "Your complete name",
    affiliatedOrg: "Affiliated Organization",
    affiliatedOrgDesc: "National Organization, Committee, Institution, Company, etc.",
    emailAddress: "Email Address",
    emailAddressDesc: "Where we'll contact you",
    yourRole: "Your Role",
    yourRoleDesc: "Position/function in your organization",
    
    contributionDetails: "Contribution Details",
    contributionType: "Contribution Type",
    contributionTypeDesc: "Select category: Research Data, Program Results, Member Insights, Institutional Partnership, Training Outcomes, Strategic Analysis, Quality Improvement, Other",
    contributionDescription: "Contribution Description",
    contributionDescriptionDesc: "Briefly describe what you're offering and why it's relevant (minimum 20 characters)",
    dataFormat: "Data Format",
    dataFormatDesc: "How is data available? (Excel, PDF, Scientific article, Link, Database, Report, Other)",
    dataScope: "Data Scope",
    dataScopeDesc: "Geographic scope and time period covered",
    verificationStatus: "Verification Status",
    verificationStatusDesc: "Is this data verified/auditable? Yes / No",
    
    authorization: "Authorization & Commitment",
    citationAuth: "I authorize use of the data provided in JCI Observatory, with formal source attribution in all published materials",
    dataOwnership: "I confirm that my organization owns or has permission to share this data",
    accuracy: "I certify that the data provided is accurate and represents the best available information",
    required: "Required",
    
    // Contribution Process Section
    processTitle: "Contribution Process",
    processSubtitle: "From submission to publication in 2-4 weeks",
    
    step1: "Contact & Submission",
    step1Desc: "Submit your proposal via email with completed form",
    step2: "Initial Review",
    step2Desc: "Our team evaluates relevance, quality, and alignment with JCI values and methodology",
    step3: "Data Verification",
    step3Desc: "We verify data sources, methodologies, and accuracy before integration",
    step4: "Integration",
    step4Desc: "Data and insights are incorporated into the Observatory platform",
    step5: "Formal Recognition",
    step5Desc: "Your contribution receives formal citation, certificate, and public acknowledgment",
    
    timeline: "Timeline: 2-4 weeks from submission to publication",
    
    // Our Commitments Section
    commitmentsTitle: "Our Commitments",
    commitmentsSubtitle: "What we promise to every contributor",
    
    transparencyProtocol: "Transparency Protocol",
    transparencyProtocolDesc: "We maintain complete transparency in all contributions: all sources cited with complete credits, organization and contributor names clearly identified, title of work/dataset and publication date documented, full link and DOI when available, complete bibliographic references, methodology and calculation formulas explained, data limitations clearly stated. Every contribution is traceable and auditable.",
    
    openMethodology: "Open Methodology",
    openMethodologyDesc: "The Observatory operates with complete methodological transparency: all frameworks and indicators documented, calculation methodologies fully explained, available for public academic audit, technical reports with complete details, calculation sheets with all formulas, FAQ addressing methodology questions, regular methodology reviews and updates. You can verify everything we publish.",
    
    formalRecognitionCommitment: "Formal Recognition",
    formalRecognitionCommitmentDesc: "Contributors receive comprehensive recognition: citation in all technical reports and publications, presentation mention at international JCI congresses, digital collaboration certificate (downloadable), recognition across JCI social media channels, feature in Observatory \"Contributors\" section, invitation to co-author special studies, priority access to new Observatory datasets. Your contribution builds your institution's credibility.",
    
    publicAccess: "Public Access",
    publicAccessDesc: "All Observatory data and insights are made freely available: interactive online dashboard, technical reports in PDF format, data files in CSV/Excel format, Creative Commons BY-NC-SA 4.0 License, free for non-commercial academic use, commercial licensing available upon request, API access for registered research partners. Your insights reach a global audience.",
    
    dataQualityStandards: "Data Quality Standards",
    dataQualityStandardsDesc: "We maintain rigorous data quality practices: all data must be verified and auditable, no synthetic or estimated data without clear notation, sources must be publicly documented, methodology must be replicable, data accuracy verified before publication, regular quality audits and reviews, correction procedures for errors. Excellence is non-negotiable.",
    
    // Contribution Types Section
    contributionTypesTitle: "Contribution Types & Examples",
    contributionTypesSubtitle: "Different ways you can contribute to the Observatory",
    
    researchData: "Research Data",
    researchDataExamples: "Academic research papers on youth leadership, longitudinal studies on member development, comparative analyses with peer organizations, survey results and statistical analyses",
    
    programResults: "Program Results",
    programResultsExamples: "JCI program outcome metrics, project impact assessments, training completion and certification data, member engagement and retention statistics",
    
    memberInsights: "Member Insights",
    memberInsightsExamples: "Member demographic data, career advancement examples, leadership skill development stories, mentorship program outcomes",
    
    institutionalPartnerships: "Institutional Partnerships",
    institutionalPartnershipsExamples: "Partnership effectiveness data, collaboration outcomes, joint program results, institutional endorsements",
    
    trainingOutcomes: "Training Outcomes",
    trainingOutcomesExamples: "Training program metrics, certification data, skill development assessments, educational alignment analyses",
    
    strategicAnalysis: "Strategic Analysis",
    strategicAnalysisExamples: "Regional trends and insights, sector-specific analyses, best practices documentation, innovation case studies",
    
    qualityImprovements: "Quality Improvements",
    qualityImprovementsExamples: "Data corrections and updates, methodology suggestions, missing information identification, process improvement recommendations",
    
    // Data Requirements Section
    dataRequirementsTitle: "Data Requirements",
    dataRequirementsSubtitle: "For contributions to be accepted, please ensure:",
    
    mandatoryElements: "Mandatory Elements",
    mandatoryList: "Clear data source identification, time period covered (date range), geographic scope (regional, national, global), data collection methodology, sample size (if applicable), verification statement, permission to share statement",
    
    recommendedElements: "Recommended Elements",
    recommendedList: "Contact person for questions, supporting documentation, related studies or references, limitations or caveats, suggested usage/interpretation, related datasets for cross-reference, update frequency (if recurring)",
    
    // FAQ Section
    faqTitle: "Frequently Asked Questions",
    faqSubtitle: "Common questions about contributing to the Observatory",
    
    faq1Q: "How long does the contribution process take?",
    faq1A: "Typically 2-4 weeks from submission to publication, depending on data complexity and required verification steps.",
    
    faq2Q: "Will my organization be credited?",
    faq2A: "Yes, absolutely. All contributions receive formal citation in every publication where they appear.",
    
    faq3Q: "Can we contribute confidential data?",
    faq3A: "Generally no—the Observatory requires public, verifiable data. However, aggregated or anonymized data may be possible. Contact us to discuss specific situations.",
    
    faq4Q: "Do we need to contribute data to use the Observatory?",
    faq4A: "No. The Observatory is free and open to everyone. Contributions are voluntary and appreciated, but not required.",
    
    faq5Q: "What happens if we find an error in our data?",
    faq5A: "We have a correction procedure. Contact us immediately and we'll update the data and publish an erratum notice.",
    
    faq6Q: "Can contributions be updated?",
    faq6A: "Yes. Annual updates are encouraged. Submit updated data anytime with a clear version history.",
    
    faq7Q: "Is there a cost to contribute?",
    faq7A: "No. Contributions are voluntary. We value your expertise and insights.",
    
    // Team & Contact Section
    teamTitle: "Our Team",
    teamSubtitle: "Contact us to start your contribution journey",
    
    observatoryTeam: "JCI Observatory Team",
    dashcityTeam: "Dashcity Team",
    email: "Email",
    website: "Website",
    contactForm: "Contact Form",
    
    developmentCouncils: "Development Councils Coordination",
    strategicCouncil: "Strategic Development Council",
    operationalCouncil: "Operational Development Council",
    educationalCouncil: "Educational Development Council",
    impactCouncil: "Impact Development Council",
    
    // Our Commitments to You Section
    partnershipTitle: "Our Commitments to You",
    partnershipSubtitle: "When you contribute to JCI Observatory, you're partnering with:",
    
    commitment1: "A global youth leadership organization with 100+ years of history and presence in 120 countries",
    commitment2: "A data-driven institution aligned with UN Sustainable Development Goals and international best practices",
    commitment3: "A credible research platform built on rigorous methodology and transparent processes",
    commitment4: "An inclusive community that values diverse perspectives and voices",
    commitment5: "A growing movement transforming how youth leaders are developed and recognized",
    
    // Why Your Data Matters Section
    whyDataMattersTitle: "Why Your Data Matters",
    whyDataMattersSubtitle: "The JCI Observatory exists to answer critical questions:",
    
    question1: "How are young leaders developed globally?",
    question2: "What skills matter most for future leaders?",
    question3: "How does JCI transform member lives?",
    question4: "What creates sustainable leadership impact?",
    question5: "How does mentorship, training, and community shape leaders?",
    
    dataMattersConclusion: "Your contribution helps answer these questions with real data, real experiences, and real impact. Together, we're building the authoritative platform for understanding youth leadership development.",
    
    // CTA Section
    ctaTitle: "Ready to Contribute?",
    ctaSubtitle: "Three Easy Steps",
    
    ctaStep1: "Fill the form with your contribution details",
    ctaStep2: "Submit to tmariano@jci.cc with any supporting files",
    ctaStep3: "Await confirmation - our team will contact you within 5 business days",
    
    questionsTitle: "Questions Before You Start?",
    questionsContact: "Email us at: observatory@jci.cc",
    questionsAlternative: "Or contact your regional Development Council representative through your National Organization.",
    
    startContributing: "Start Contributing",
    contactUs: "Contact Us",
    
    // Checklist
    checklistTitle: "Contribution Checklist",
    checklistSubtitle: "Before submitting, verify you have:",
    
    checkItem1: "Complete contributor information",
    checkItem2: "Clear contribution type selected",
    checkItem3: "Data source documentation",
    checkItem4: "Time period and geographic scope defined",
    checkItem5: "Verification/audit statement",
    checkItem6: "Authorization to share",
    checkItem7: "Supporting files ready",
    checkItem8: "Contact information for follow-up",
    
    // Footer info
    lastUpdated: "Last Updated: December 2025",
    availableLanguages: "Available in English, Portuguese, Spanish, French, and Japanese",
    joinCommunity: "Join the community of contributors building the future of youth leadership research!",
  },
} as const;
